// @ts-check

/**
 * @file Provides the project board delete confirmation modal.
 */
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {memo} from "react"
import {Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import Modal from "../../../components/modal"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} ProjectBoardDeleteConfirmModalProps
 * @property {string} bodyText Confirmation message body.
 * @property {string} confirmLabel Confirm button label.
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {(...args: unknown[]) => unknown} onCancelPress Cancel press handler.
 * @property {(...args: unknown[]) => unknown} onConfirmPress Confirm press handler.
 * @property {boolean} saving Whether the confirm action is running.
 * @property {string} [testID] Stable test id.
 * @property {string} title Modal title.
 */

/** @typedef {Record<string, never>} ProjectBoardDeleteConfirmModalState */

/**
 * Project board delete confirmation modal.
 * @augments {ShapeComponent<ProjectBoardDeleteConfirmModalProps, ProjectBoardDeleteConfirmModalState>}
 */
class ProjectBoardDeleteConfirmModal extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = PropTypesExact({
    bodyText: PropTypes.string.isRequired,
    confirmLabel: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    onCancelPress: PropTypes.func.isRequired,
    onConfirmPress: PropTypes.func.isRequired,
    saving: PropTypes.bool.isRequired,
    testID: PropTypes.string,
    title: PropTypes.string.isRequired
  })
  static defaultProps = {
    testID: undefined
  }

  /** Initializes locale hooks for translated labels. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the delete confirmation modal shell.
   * @returns {ReactNode} Delete confirmation content.
   */
  render() {
    const testID = this.p.testID
    const colors = this.tt.colors
    const themeName = this.tt.themeName
    return (
      <Modal
        cardStyle={styles[`card-${themeName}`] ||= {
          borderColor: colors.danger,
          maxWidth: 420,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onCancelPress}
        testID={testID}
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.dangerText,
              fontSize: 20,
              fontWeight: 700,
              marginBottom: 12
            }}
            testID={testID ? `${testID}/title` : undefined}
          >
            {this.p.title}
          </Text>
          <Text
            style={styles[`body-${themeName}`] ||= {
              color: colors.textMuted,
              fontSize: 15,
              lineHeight: 22,
              marginBottom: 20
            }}
            testID={testID ? `${testID}/body` : undefined}
          >
            {this.p.bodyText}
          </Text>
          <View
            style={styles.actions ||= {
              flexDirection: "row",
              gap: 10,
              justifyContent: "flex-end"
            }}
            testID={testID ? `${testID}/actions` : undefined}
          >
            <Pressable
              onPress={this.p.onCancelPress}
              style={styles[`cancelButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.surfaceMuted,
                borderColor: colors.border,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID={testID ? `${testID}/cancelButton` : undefined}
            >
              <View
                style={styles.cancelButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID={testID ? `${testID}/cancelButton/icon` : undefined}
              >
                <FontAwesome6 color={colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID={testID ? `${testID}/cancelButton/text` : undefined}
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.p.onConfirmPress}
              style={styles[`confirmButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.danger,
                borderRadius: 10,
                flexDirection: "row",
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID={testID ? `${testID}/confirmButton` : undefined}
            >
              <View
                style={styles.confirmButtonIcon ||= {
                  marginRight: 8,
                  width: 14
                }}
                testID={testID ? `${testID}/confirmButton/icon` : undefined}
              >
                <FontAwesome6 color={colors.onPrimary} name="trash" size={14} />
              </View>
              <Text
                style={styles[`confirmButtonText-${themeName}`] ||= {
                  color: colors.onPrimary,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID={testID ? `${testID}/confirmButton/text` : undefined}
              >
                {this.p.saving ? _("Saving...") : this.p.confirmLabel}
              </Text>
            </Pressable>
          </View>
      </Modal>
    )
  }
}

/** @type {NamedExoticComponent<ProjectBoardDeleteConfirmModalProps>} */
const ProjectBoardDeleteConfirmModalComponent = memo(shapeComponent(ProjectBoardDeleteConfirmModal))

export default ProjectBoardDeleteConfirmModalComponent
